import type { IWindowService } from 'angular';
import type { StateParams } from '@uirouter/angularjs';
import type { ConfigService } from './mx-config-service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { I18nService } from '../../i18n/i18n.service';
import type { MxApplication, ApplicationProvider } from '../model/application';
import { EventCodes } from '../model/event-codes';
import type { EventService } from './event-service';

/**
 * @ngdoc service
 * @name MxApplicationService
 * @module mx.shell
 *
 * @requires <a href="https://docs.angularjs.org/api/ng/service/$window" target="_blank">$window</a>
 * @requires <a href="https://github.com/angular-ui/ui-router/wiki/URL-Routing" target="_blank">$stateParams</a>
 * @requires {@ref mx.shell.Config mx.shell.Config}
 * @requires {@ref mx.internationalization mx.internationalization}
 * @requires {@ref mx.shell.ApplicationProviderService mx.shell.ApplicationProviderService}
 *
 * @description
 * A service prepares application items and provides method to get them
 */
export class MxApplicationService {
	static $inject = ['$window', '$stateParams', 'mx.shell.Config', 'mx.internationalization', 'mx.shell.InternationalizationService', 'mx.shell.EventService'];

	private applicationProviderService: ApplicationProvider;
	private appItems: MxApplication[];
	private isLoadedAppItems = false;
	private getCurrentAppPromise?: Promise<MxApplication>;

	constructor(
		private readonly $window: IWindowService,
		private readonly $stateParams: StateParams,
		private readonly configService: ConfigService,
		private readonly i18n: I18n,
		private readonly i18nService: I18nService,
		private readonly eventService: EventService
	) {
		this.applicationProviderService = {
			load: async () => [],
		};

		this.appItems = [];
		this.isLoadedAppItems = false;
	}

	/**
	 * @ngdoc method
	 * @name MxApplicationService#setApplicationProvider
	 *
	 * @param {ApplicationProvider} applicationProvider - provider to load applications
	 *
	 * @description
	 * Set ApplicationProvider which is used to load applications
	 */
	setApplicationProvider(applicationProvider?: ApplicationProvider) {
		if (typeof applicationProvider === 'object' && typeof applicationProvider.load === 'function') {
			this.applicationProviderService = applicationProvider;
		}
	}

	/**
	 * @ngdoc method
	 * @name MxApplicationService#isEnabled
	 *
	 * @description
	 * Signals whether the Apps button is present in the Navigation panel
	 *
	 * @returns {promise} Is Apps button available. Resolves to boolean
	 */
	isEnabled() {
		return Boolean((this.configService.get('toolbar') || {}).showAppsButton);
	}

	reloadApplications() {
		this.appItems = [];
		this.isLoadedAppItems = false;
		this.getCurrentAppPromise = undefined;
		this.getCurrentApplication(true);
	}

	getApplications() {
		return this.appItems;
	}

	isApplicationsButtonVisible() {
		return this.appItems.length > 1;
	}

	getCurrentApplication(reload = false) {
		if (!this.getCurrentAppPromise) {
			this.getCurrentAppPromise = this.applicationProviderService.load(reload).then((result) => {
				this.appItems = (result || []).sort((a, b) => (a.position || 0) - (b.position || 0));

				const currentApp = this.getCurrentApplicationSync();

				if (!this.isLoadedAppItems) {
					this.appItems.forEach((item) => this.addApplication(item, currentApp));

					this.isLoadedAppItems = true;
				}

				return currentApp;
			});
		}
		return this.getCurrentAppPromise;
	}

	setApplication(app: MxApplication, inNewWindow = false) {
		const newUrl = app.url || 'app-' + app.name;
		const basePath = this.configService.get('basePath');
		let resUrl = (basePath === '/' ? '' : basePath) + newUrl;
		const loc = location.origin;

		resUrl = resUrl.indexOf('http') === 0 ? resUrl : loc + (loc[loc.length - 1] === '/' || resUrl[0] === '/' ? '' : '/') + resUrl;

		if (inNewWindow) {
			window.open(resUrl);
		} else {
			location.href = resUrl;
		}
	}

	getCurrentApplicationSync() {
		const { $stateParams, $window, appItems } = this;
		const appString = $stateParams.appString || ($window.location.pathname.match(/\/app-([^/^#^?]*)/) || [])[1];
		const currentApp =
			(appString && appItems.find((app) => app.name && app.name.toUpperCase() === appString.toUpperCase())) ||
			(Array.isArray(appItems) &&
				appItems.length > 0 &&
				appItems.reduce((minApp, app) => {
					return (minApp.position || 0) > (app.position || 0) ? app : minApp;
				}));

		return currentApp;
	}

	addApplication(appItem: MxApplication, currentApp: MxApplication) {
		if (!appItem) {
			throw new Error(this.i18n.get('shell.errors.appItem_parameter_not_provided'));
		}

		const defaultItemClick = ($event: MouseEvent) => {
			let isClickModifierUsed;
			// $event check to handle programmatic click (for command palette launcher)

			if ($event) {
				// If click with ctrl or mouse-middle-button
				isClickModifierUsed = ($event.which === 1 && ($event.ctrlKey || $event.metaKey)) || $event.which === 2;
			} else {
				isClickModifierUsed = false;
			}

			this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
				eventName: 'Application switched',
				sender: appItem.value,
				senderTitle: appItem.name,
			});

			this.setApplication(appItem, isClickModifierUsed);
		};

		appItem.onItemClick = appItem.onItemClick || defaultItemClick;

		if (!appItem.expIsChecked) {
			appItem.isChecked = appItem.title === currentApp.title;
		}
	}

	async getWindowTitle(): Promise<string> {
		const { i18n, configService } = this;
		const defaultWindowTitle = i18n.get('solutionBuilderAgent.pageTitle.defaultTitle');
		const windowTitles = configService.get('defaultWindowTitles');

		if (!Array.isArray(windowTitles)) {
			return defaultWindowTitle;
		}

		const language = await this.i18nService.currentLanguage;
		if (language) {
			return (windowTitles.find((titleObject) => titleObject.lcid === language.parentLcid || titleObject.lcid === language.lcid) || {}).title || defaultWindowTitle;
		}

		return defaultWindowTitle;
	}
}
